import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import AlertsPanel from '../AlertsPanel/AlertsPanel';
import { changePassword } from '../../services/api';
import './Header.css';

const errorMessage = (err: any, fallback: string): string =>
  err?.response?.data?.detail ?? fallback;

interface PasswordChangeDraft {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, impersonating, stopImpersonation } = useAuthStore();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // ── Zmień hasło (2026-09-20) — dostępne dla KAŻDEGO zalogowanego, nie
  // tylko admina, bo user zmienia wyłącznie własne hasło.
  const [passwordDraft, setPasswordDraft] = useState<PasswordChangeDraft | null>(null);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleStopImpersonation = () => {
    stopImpersonation();
    navigate('/admin/clients');
  };

  const isActive = (path: string) => location.pathname === path;

  const handleNavClick = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const openPasswordModal = () => {
    setPasswordDraft({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError(null);
    setPasswordSuccess(false);
    setDrawerOpen(false);
  };

  const closePasswordModal = () => {
    if (passwordSaving) return;
    setPasswordDraft(null);
    setPasswordError(null);
    setPasswordSuccess(false);
  };

  const handleChangePassword = async () => {
    if (!passwordDraft || passwordSaving) return;
    const { currentPassword, newPassword, confirmPassword } = passwordDraft;

    if (!currentPassword || !newPassword || !confirmPassword) return;

    // Walidacja lokalna PRZED wysłaniem requestu — backend i tak waliduje
    // ponownie (min. 8 znaków), ale to daje szybszy feedback bez round-tripu.
    if (newPassword.length < 8) {
      setPasswordError('Nowe hasło musi mieć co najmniej 8 znaków');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Nowe hasła nie są identyczne');
      return;
    }

    setPasswordSaving(true);
    setPasswordError(null);
    try {
      await changePassword({ current_password: currentPassword, new_password: newPassword });
      setPasswordSuccess(true);
      setTimeout(() => {
        setPasswordDraft(null);
        setPasswordSuccess(false);
      }, 1500);
    } catch (err: any) {
      setPasswordError(errorMessage(err, 'Nie udało się zmienić hasła'));
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <>
      {impersonating && (
        <div className="impersonation-banner">
          <span>Oglądasz jako: <strong>{user?.email}</strong></span>
          <button onClick={handleStopImpersonation}>Wróć do panelu admina</button>
        </div>
      )}
      <header className="app-header">
        <div className="header-left">
          <div className="logo"><img src="/logo.svg" alt="10drive" style={{ height: '36px', width: 'auto' }} /></div>
          <nav className="main-nav">
            <button
              className={`nav-item ${isActive('/dashboard') ? 'active' : ''}`}
              onClick={() => navigate('/dashboard')}
            >Mapa</button>
            <button
              className={`nav-item ${isActive('/reports') ? 'active' : ''}`}
              onClick={() => navigate('/reports')}
            >Raporty</button>
            <button
              className={`nav-item ${isActive('/geofences') ? 'active' : ''}`}
              onClick={() => navigate('/geofences')}
            >Strefy</button>
            <AlertsPanel />
            <button
              className={`nav-item ${isActive('/settings') ? 'active' : ''}`}
              onClick={() => navigate('/settings')}
            >Ustawienia</button>
          </nav>
        </div>

        <div className="header-right">
          <span className="user-info">{user?.name}</span>
          {user?.administrator && (
            <button className="admin-btn" onClick={() => navigate('/admin')}>
              Admin Panel
            </button>
          )}
          {user?.administrator && (
            <button className="admin-btn" onClick={() => navigate('/admin/clients')}>
              Klienci
            </button>
          )}
          <a className="help-btn" href="/api/help/instrukcja" download="instrukcja-10drive.md" title="Pobierz instrukcję obsługi">?</a>
          <button className="password-btn" onClick={openPasswordModal}>
            Zmień hasło
          </button>
          <button className="logout-btn" onClick={handleLogout}>
            Wyloguj
          </button>
          <button className="hamburger-btn" onClick={() => setDrawerOpen(true)} aria-label="Menu">
            ☰
          </button>
        </div>
      </header>

      {drawerOpen && (
        <>
          <div className="mobile-overlay" onClick={() => setDrawerOpen(false)} />
          <nav className="mobile-drawer">
            <div className="mobile-drawer-head">
              <div className="logo"><img src="/logo.svg" alt="10drive" style={{ height: '30px', width: 'auto' }} /></div>
              <button className="mobile-drawer-close" onClick={() => setDrawerOpen(false)}>✕</button>
            </div>
            <button
              className={`mobile-nav-item ${isActive('/dashboard') ? 'active' : ''}`}
              onClick={() => handleNavClick('/dashboard')}
            >Mapa</button>
            <button
              className={`mobile-nav-item ${isActive('/reports') ? 'active' : ''}`}
              onClick={() => handleNavClick('/reports')}
            >Raporty</button>
            <button
              className={`mobile-nav-item ${isActive('/geofences') ? 'active' : ''}`}
              onClick={() => handleNavClick('/geofences')}
            >Strefy</button>
            <div className="mobile-nav-alerts" onClick={() => setDrawerOpen(false)}>
              <AlertsPanel />
            </div>
            <button
              className={`mobile-nav-item ${isActive('/settings') ? 'active' : ''}`}
              onClick={() => handleNavClick('/settings')}
            >Ustawienia</button>
            {user?.administrator && (
              <button
                className={`mobile-nav-item ${isActive('/admin') ? 'active' : ''}`}
                onClick={() => handleNavClick('/admin')}
              >Admin Panel</button>
            )}
            {user?.administrator && (
              <button
                className={`mobile-nav-item ${isActive('/admin/clients') ? 'active' : ''}`}
                onClick={() => handleNavClick('/admin/clients')}
              >Klienci</button>
            )}
            <button className="mobile-nav-item" onClick={openPasswordModal}>
              Zmień hasło
            </button>
          </nav>
        </>
      )}

      {passwordDraft && (
        <div className="password-modal-backdrop" onClick={closePasswordModal}>
          <div className="password-modal" onClick={e => e.stopPropagation()}>
            <h3>Zmień hasło</h3>
            {passwordError && <div className="password-modal-error">{passwordError}</div>}
            {passwordSuccess ? (
              <div className="password-modal-success">✓ Hasło zostało zmienione</div>
            ) : (
              <>
                <label className="password-field">
                  <span>Obecne hasło</span>
                  <input
                    type="password"
                    value={passwordDraft.currentPassword}
                    onChange={e => setPasswordDraft({ ...passwordDraft, currentPassword: e.target.value })}
                    autoFocus
                  />
                </label>
                <label className="password-field">
                  <span>Nowe hasło</span>
                  <input
                    type="password"
                    value={passwordDraft.newPassword}
                    onChange={e => setPasswordDraft({ ...passwordDraft, newPassword: e.target.value })}
                  />
                </label>
                <label className="password-field">
                  <span>Potwierdź nowe hasło</span>
                  <input
                    type="password"
                    value={passwordDraft.confirmPassword}
                    onChange={e => setPasswordDraft({ ...passwordDraft, confirmPassword: e.target.value })}
                  />
                </label>
                <div className="password-modal-actions">
                  <button onClick={closePasswordModal} disabled={passwordSaving}>Anuluj</button>
                  <button
                    onClick={handleChangePassword}
                    disabled={
                      !passwordDraft.currentPassword ||
                      !passwordDraft.newPassword ||
                      !passwordDraft.confirmPassword ||
                      passwordSaving
                    }
                  >
                    {passwordSaving ? 'Zmienianie...' : 'Zmień hasło'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
